import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Route, Phone, Mail, CalendarX2, EyeOff, Bus } from 'lucide-react';
import { PeriodPicker, PeriodValue, weekLabel, weekFromLabel } from '@/components/all-trips/PeriodPicker';
import { usePublicLanguage } from '@/components/public/usePublicLanguage';
import PublicLanguageToggle from '@/components/public/PublicLanguageToggle';
import { DriverPlanDayCard, ManifestLink, type PlanDay } from '@/components/public/DriverPlanDayCard';
import { TripNoteEditor } from '@/components/public/TripNoteEditor';
import { WeeklyDateView } from '@/components/all-trips/WeeklyDateView';
import { TypeLegend } from '@/components/all-trips/TypeLegend';
import { typeCodesInPlan } from '@/components/all-trips/tripType';
import { addDays, isoLocal, mondayOf } from '@/components/routeplans/routeplanWeek';
import { makeLocationColorResolver } from '@/lib/locationColors';
import { abbreviateLocation } from '@/lib/abbreviations';
import { useMediaQuery } from '@/hooks/useMediaQuery';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_DAYS = 184;
const noop = () => {};

const Brand = ({ company, toggle }: { company: any; toggle: React.ReactNode }) => (
  <div className="relative text-center mb-4" data-testid="sp-brand">
    <div className="absolute right-0 top-0">{toggle}</div>
    {company?.logo && <img src={company.logo} alt={company.name || ''} className="h-10 mx-auto mb-1 object-contain" />}
    {company?.name && <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{company.name}</p>}
    {(company?.phone || company?.email) && (
      <p className="text-[11px] text-slate-400 mt-0.5 inline-flex items-center gap-3">
        {company.phone && <a href={`tel:${company.phone}`} className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{company.phone}</a>}
        {company.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{company.email}</span>}
      </p>
    )}
  </div>
);

const StateCard = ({ company, title, message, toggle }: any) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="sp-state-card">
      <Brand company={company} toggle={toggle} />
      <h1 className="text-lg font-bold text-slate-800 mb-1">{title}</h1>
      <p className="text-sm text-slate-500">{message}</p>
    </div>
  </div>
);

function periodFromParams(sp: URLSearchParams): PeriodValue {
  const fra = sp.get('fra'), til = sp.get('til'), uke = sp.get('uke');
  if (fra && til) return { mode: 'range', weekStart: isoLocal(mondayOf(new Date(`${fra}T00:00:00`))), from: fra, to: til };
  const ws = (uke && weekFromLabel(uke)) || isoLocal(mondayOf(new Date()));
  return { mode: 'week', weekStart: ws, from: ws, to: isoLocal(addDays(new Date(`${ws}T00:00:00`), 6)) };
}

// Sjåførens/eksternt selskaps kjøreplan (/sp/:token): offentlig, uten innlogging, alle egne turer i valgt periode. Historikk alltid,
// kommende dager kun når de er publisert til sjåfør. Hver tur åpner sin passasjerliste (/pl/) med avhuking.
export default function PublicDriverPlanPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [sp, setSp] = useSearchParams();
  const period = useMemo(() => periodFromParams(sp), [sp]);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState('');
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const { lang, setPublicLanguage } = usePublicLanguage(null);
  const toggle = <PublicLanguageToggle lang={lang} onChange={setPublicLanguage} />;

  useEffect(() => {
    const meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const prev = meta?.content;
    if (meta) meta.content = 'noindex, nofollow';
    return () => { if (meta && prev !== undefined) meta.content = prev; };
  }, []);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    setErr('');
    try {
      const q = period.mode === 'range' ? `fra=${period.from}&til=${period.to}` : `uke=${weekLabel(period.weekStart)}`;
      const res = await fetch(`${API}/api/public/driver-plan/${token}?${q}`, { cache: 'no-store' });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 400) setErr(j?.detail || t('publicCustomerPlan.ugyldigPeriode', 'Ugyldig periode'));
        else { setErr('invalid'); setData(null); }
        return;
      }
      setData(j); setUpdatedAt(new Date());
    } catch { setErr(silent ? t('publicCustomerPlan.kunneIkkeOppdatere', 'Kunne ikke oppdatere akkurat nå – viser sist hentede plan') : 'invalid'); }
    finally { setLoading(false); setRefreshing(false); }
  }, [token, period.mode, period.from, period.to, period.weekStart, t]);
  useEffect(() => { load(); }, [load]);

  const onPeriodChange = (v: PeriodValue) => {
    const next = new URLSearchParams();
    if (v.mode === 'range') { next.set('fra', v.from); next.set('til', v.to); } else next.set('uke', weekLabel(v.weekStart));
    const l = sp.get('lang'); if (l) next.set('lang', l);
    const b = sp.get('buss'); if (b) next.set('buss', b);
    setSp(next, { replace: true });
  };
  // Iter 832: bussfilter — valget ligger i lenken (?buss=…) så én lenke kan deles per buss. '' = alle, '-' = uten kjøretøy.
  const bus = sp.get('buss') || '';
  const setBus = (v: string) => {
    const next = new URLSearchParams(sp);
    if (v) next.set('buss', v); else next.delete('buss');
    setSp(next, { replace: true });
  };

  // Iter 802: merknad per tur → kontoret (Ukesplan + varsel). Tom tekst fjerner merknaden.
  const saveNote = useCallback(async (tripNumber: string, note: string) => {
    try {
      const res = await fetch(`${API}/api/public/driver-plan/${token}/notes`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ trip_number: tripNumber, note }) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j?.detail || 'error');
      setData((d: any) => d && ({ ...d, days: d.days.map((day: any) => ({ ...day, trips: (day.trips || []).map((tr: any) => (tr.trip_number === tripNumber ? { ...tr, driver_note: j.note ? { note: j.note, updated_at: j.updated_at, by: j.by } : null } : tr)) })) }));
      toast.success(j.note ? t('publicDriverPlan.merknadSendt', 'Merknaden er sendt til kontoret') : t('publicDriverPlan.merknadFjernet', 'Merknaden er fjernet'));
      return true;
    } catch (e: any) {
      toast.error(e?.message === 'error' ? t('publicDriverPlan.merknadFeilet', 'Kunne ikke lagre merknaden – prøv igjen') : e?.message);
      return false;
    }
  }, [token, t]);

  // Stoppfarger som i bookinglisten (forkortelser m/ farge fra kontoret) — kart følger med i payloaden.
  const locColor = useMemo(() => makeLocationColorResolver(data?.location_colors, data?.location_shortcuts), [data?.location_colors, data?.location_shortcuts]);
  const locShort = useMemo(() => {
    const map: Record<string, string> = data?.location_shortcuts || {};
    return (loc: string) => abbreviateLocation(loc, map);
  }, [data?.location_shortcuts]);
  // Iter 831: samme rutenett som Ukesplanen på nettbrett/PC; kortvisning på mobil.
  const wide = useMediaQuery('(min-width: 768px)');
  const allVisibleDays: PlanDay[] = useMemo(() => (data?.days || []).filter((d: PlanDay) => d.visible), [data?.days]);
  const vehicles: string[] = useMemo(() => {
    const fromApi: string[] = data?.vehicles || [];
    const seen = new Set(fromApi);
    allVisibleDays.forEach((d) => d.trips.forEach((tr) => { const v = String(tr.vehicle || '').trim(); if (v) seen.add(v); }));
    return Array.from(seen).sort((a, b) => a.localeCompare(b, 'nb'));
  }, [data?.vehicles, allVisibleDays]);
  const hasUnassignedVehicle = useMemo(() => allVisibleDays.some((d) => d.trips.some((tr) => !String(tr.vehicle || '').trim())), [allVisibleDays]);
  const visibleDays: PlanDay[] = useMemo(() => {
    if (!bus) return allVisibleDays;
    const match = (tr: any) => (bus === '-' ? !String(tr.vehicle || '').trim() : String(tr.vehicle || '').trim() === bus);
    // Med bussfilter skjules dager uten turer for bussen — en per-buss-lenke skal ikke bli seks tomme blokker.
    return allVisibleDays
      .map((d) => { const trips = d.trips.filter(match); return { ...d, trips, trip_count: trips.length }; })
      .filter((d) => d.trips.length > 0);
  }, [allVisibleDays, bus]);
  const plan = useMemo(() => ({
    days: visibleDays.map((d) => ({ ...d, trips: d.trips.map((tr) => ({ ...tr, plan_status: tr.cancelled ? 'innstilt' : undefined })) })),
  }), [visibleDays]);
  const maxStops = useMemo(() => Math.max(2, ...visibleDays.flatMap((d) => d.trips.map((tr) => tr.stops?.length || 0))), [visibleDays]);
  const extraColumns = useMemo(() => [
    { key: 'liste', header: t('driverPlanPublish.listeKolonne', 'Liste'), cell: (tr: any) => <ManifestLink trip={tr} compact /> },
    { key: 'merknad', header: t('publicDriverPlan.merknad', 'Merknad'), className: 'min-w-[11rem]', cell: (tr: any) => <TripNoteEditor tripNumber={tr.trip_number} note={tr.driver_note} onSave={saveNote} lang={lang} /> },
  ], [t, saveNote, lang]);

  if (loading && !data) return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if ((err === 'invalid' || !data) && !refreshing) {
    return <StateCard company={{}} toggle={toggle} title={t('driverManifest.lenkenErIkkeGyldig', 'Lenken er ikke gyldig')} message={t('driverManifest.lenkenUgyldigEllerUtlopt', 'Lenken er ugyldig eller utløpt. Kontakt kontoret for en ny lenke.')} />;
  }
  const company = data.company || {};
  if (data.state === 'revoked') return <StateCard company={company} toggle={toggle} title={t('driverManifest.lenkenErTrukket', 'Lenken er ikke lenger aktiv')} message={t('publicDriverPlan.lenkenTrukketMelding', 'Kjøreplan-lenken er trukket tilbake. Kontakt kontoret for en ny lenke.')} />;
  if (data.state === 'expired') return <StateCard company={company} toggle={toggle} title={t('driverManifest.lenkenErUtlopt', 'Lenken er utløpt')} message={t('publicDriverPlan.lenkenUtloptMelding', 'Kjøreplan-lenken er utløpt. Kontakt kontoret for en ny lenke.')} />;
  if (data.state === 'not_yet_active') return <StateCard company={company} toggle={toggle} title={t('publicCustomerPlan.ikkeAktivEnna', 'Lenken er ikke aktiv ennå')} message={t('publicCustomerPlan.komTilbakeSenere', 'Kom tilbake senere.')} />;

  const days: PlanDay[] = visibleDays;
  const today = new Date();
  const minDate = isoLocal(addDays(today, -MAX_DAYS)), maxDate = isoLocal(addDays(today, MAX_DAYS));
  const clock = updatedAt ? updatedAt.toLocaleTimeString(lang === 'en' ? 'en-GB' : 'nb-NO', { hour: '2-digit', minute: '2-digit' }) : '';
  const totalTrips = days.reduce((s, d) => s + d.trips.length, 0);
  const totalPax = days.reduce((s, d) => s + d.trips.reduce((x, tr) => x + (tr.total_passengers || 0), 0), 0);
  const isAll = data.kind === 'all';
  // Filteret vises alltid når et bussvalg ligger i lenken (så det kan nullstilles), ellers når det finnes noe å filtrere på.
  const showBusFilter = !!bus || isAll || vehicles.length > 1 || (vehicles.length > 0 && hasUnassignedVehicle);
  const busOptions = bus && bus !== '-' && !vehicles.includes(bus) ? [bus, ...vehicles] : vehicles;
  const busLabel = bus === '-' ? t('publicDriverPlan.utenKjoretoy', 'Uten kjøretøy') : bus;

  return (
    <div className="min-h-screen bg-slate-50 py-4 px-3 sm:px-6" data-testid="sp-page" data-layout={wide ? 'table' : 'cards'}>
      <div className={wide ? 'max-w-[1800px] mx-auto' : 'max-w-7xl mx-auto'}>
        <Brand company={company} toggle={toggle} />
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 mb-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-slate-800 inline-flex items-center gap-2" data-testid="sp-title"><Route className="w-5 h-5 text-violet-600" />{isAll ? t('publicDriverPlan.tittelAlle', 'Kjøreplan – alle turer') : t('publicDriverPlan.tittel', 'Kjøreplan')}</h1>
              <p className="text-sm text-slate-500 mt-0.5" data-testid="sp-recipient">
                {isAll ? (bus ? `${t('publicDriverPlan.buss', 'Buss')} ${busLabel}` : t('publicDriverPlan.alleBusser', 'Alle busser')) : data.recipient_name}
                {data.kind === 'external' ? ` · ${t('publicDriverPlan.eksterntSelskap', 'eksternt selskap')}` : ''}
              </p>
            </div>
            <div className="text-right text-xs text-slate-500">
              <button type="button" onClick={() => load(true)} disabled={refreshing} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 font-medium" data-testid="sp-refresh">
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />{t('driverManifest.oppdater', 'Oppdater')}
              </button>
              {clock && <div className="mt-0.5" data-testid="sp-updated-at">{t('driverManifest.sistOppdatert', 'Sist oppdatert')} {clock}</div>}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <PeriodPicker value={period} onChange={onPeriodChange} maxDays={data.max_days || MAX_DAYS} minDate={minDate} maxDate={maxDate} testIdPrefix="sp" />
              {showBusFilter && (
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-600" data-testid="sp-bus-filter">
                  <Bus className="w-4 h-4 text-violet-600" />
                  <select
                    value={bus}
                    onChange={(e) => setBus(e.target.value)}
                    className={`h-9 rounded-md border bg-white px-2 text-sm text-slate-800 ${bus ? 'border-violet-400 ring-1 ring-violet-200' : 'border-slate-300'}`}
                    aria-label={t('publicDriverPlan.filtrerBuss', 'Filtrer per buss')}
                    data-testid="sp-bus-select"
                  >
                    <option value="">{t('publicDriverPlan.alleBusser', 'Alle busser')}</option>
                    {busOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                    {(hasUnassignedVehicle || bus === '-') && <option value="-">{t('publicDriverPlan.utenKjoretoy', 'Uten kjøretøy')}</option>}
                  </select>
                  {bus && (
                    <button type="button" onClick={() => setBus('')} className="text-violet-700 underline" data-testid="sp-bus-clear">{t('publicDriverPlan.nullstill', 'Vis alle')}</button>
                  )}
                </label>
              )}
            </div>
            <div className="text-sm font-semibold text-slate-700" data-testid="sp-summary">
              {totalTrips} {totalTrips === 1 ? t('trips.tripLowercase', 'tur') : t('trips.tripsLowercase', 'turer')}, {totalPax} {t('publicDriverPlan.passasjerer', 'passasjerer')}
              {bus && <span className="ml-1.5 font-normal text-slate-500" data-testid="sp-summary-bus">· {t('publicDriverPlan.buss', 'Buss')} {busLabel}</span>}
            </div>
          </div>
          {err && err !== 'invalid' && <p className="mt-2 text-xs text-red-600" data-testid="sp-range-error">{err}</p>}
          {bus && totalTrips === 0 && (
            <p className="mt-2 text-xs text-amber-700 inline-flex items-center gap-1" data-testid="sp-bus-empty">
              <Bus className="w-3 h-3" />{t('publicDriverPlan.ingenTurerForBuss', { defaultValue: 'Ingen turer for buss {{bus}} i valgt periode.', bus: busLabel })}
            </p>
          )}
          {data.hidden_future_days > 0 && (
            <p className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1" data-testid="sp-hidden-days">
              <EyeOff className="w-3 h-3" />{t('publicDriverPlan.skjulteDager', { defaultValue: '{{count}} kommende dag(er) i perioden er ikke publisert ennå og vises når kontoret publiserer dem.', count: data.hidden_future_days })}
            </p>
          )}
        </div>

        {days.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-10 text-center text-slate-500" data-testid="sp-empty">
            <CalendarX2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm">{bus ? t('publicDriverPlan.ingenTurerForBuss', { defaultValue: 'Ingen turer for buss {{bus}} i valgt periode.', bus: busLabel }) : t('publicCustomerPlan.ingenTurerIValgtPeriode', 'Ingen publiserte dager å vise i valgt periode ennå')}</p>
          </div>
        ) : wide ? (
          // Samme rutenett og kolonner som Ukesplanen (forkortelser, stoppfarger, Flynr/Flytid, Pax m/ opptalt, Sjåfør, Kjøretøy) + Liste og Merknad.
          <div className="space-y-4 bg-white rounded-2xl shadow-sm p-3 sm:p-4" data-testid="sp-grid">
            <TypeLegend codes={typeCodesInPlan({ days })} lang={lang} testId="sp-type-legend" />
            <WeeklyDateView
              weeklyPlan={plan} isStaff={false} readOnly hideNotes maxStopsInWeek={maxStops}
              selectedDrivers={[]} showUnassignedOnly={false}
              selectedTrips={new Set()} setSelectedTrips={noop} expandedRows={new Set()} setExpandedRows={noop}
              getLocationColor={locColor} getLocationShortName={locShort}
              getDriverColor={() => null} getDriverDisplayName={(n: string) => n} getCompanyShortName={(n: string) => n}
              handleViewDetails={noop} setSeatRequestModal={noop}
              extraColumns={extraColumns} hideDriverNoteBadge
            />
          </div>
        ) : (
          <div className="space-y-3" data-testid="sp-days">
            <TypeLegend codes={typeCodesInPlan({ days })} lang={lang} testId="sp-type-legend" />
            {days.map((d) => <DriverPlanDayCard key={d.date} day={d} lang={lang} onSaveNote={saveNote} locColor={locColor} />)}
          </div>
        )}
        <p className="text-[11px] text-slate-400 text-center mt-6" data-testid="sp-footer">{t('publicDriverPlan.fotnote', 'Planen oppdateres fortløpende av kontoret. Trykk «Passasjerliste» på en tur for å se navnene og krysse av om bord.')}</p>
      </div>
    </div>
  );
}
